'use client';

import { OptimizedImage } from '@/components/OptimizedImage/OptimizedImage';
import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@/data/products';
import { useCart } from '@/context/cart';
import { useToast } from '@/components/Toast/Toast';
import { IconCart, IconArrowRight } from '@/components/Icons/Icons';

function formatTRY(value: number) {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value} TL`;
  }
}

function renderNameWithMmHighlight(name: string) {
  const parts = name.split(/(\d+\s*mm|\d+\s*dns|yumurta|piramit|labirent|düz)/gi);
  return parts.map((part, index) =>
    /^\d+\s*(mm|dns)$/i.test(part) || /^(yumurta|piramit|labirent|düz)$/i.test(part) ? (
      <span key={`${part}-${index}`} className="ProductCard__mm">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

export const ProductCard = memo(function ProductCard({ product, variant }: { product: Product; variant?: 'default' | 'catalog' }) {
  const cart = useCart();
  const toast = useToast();

  return (
    <article className={`ProductCard ${variant === 'catalog' ? 'ProductCard--catalog' : ''}`}>
      <div className="ProductCard__mediaWrapper">
        <Link to={`/${product.slug}`} aria-label={product.name}>
          <OptimizedImage className="ProductCard__media" src={product.imagePath} alt={product.name} loading="lazy" variant="card" />
        </Link>
      </div>
      <div className="ProductCard__body">
        <h3 className="ProductCard__title">
          <Link to={`/${product.slug}`}>{renderNameWithMmHighlight(product.name)}</Link>
        </h3>
        <p className="ProductCard__meta">{product.shortDescription}</p>
        <div className="ProductCard__features">
          {product.thicknessMm && (
            <div className="ProductCard__feature">
              <span className="ProductCard__featureLabel">Kalınlık:</span>
              <span className="ProductCard__featureValue ProductCard__mm">{product.thicknessMm}mm</span>
            </div>
          )}
          <div className="ProductCard__feature">
            <span className="ProductCard__featureLabel">Kategori:</span>
            <span className="ProductCard__featureValue">{product.category}</span>
          </div>
          <div className="ProductCard__feature">
            <span className="ProductCard__featureLabel">Fiyat:</span>
            {typeof product.priceTRY === 'number' ? (
              <span className="ProductCard__featureValue ProductCard__priceInline">
                {formatTRY(product.priceTRY)}
                {product.priceUnit === 'm2' ? <span className="ProductCard__priceUnitInline">/m²</span> : null}
              </span>
            ) : (
              <span className="ProductCard__featureValue ProductCard__priceInline">Teklif Al</span>
            )}
          </div>
        </div>
        <div className="ProductCard__actions">
          <Link className="btn ProductCard__detailBtn" to={`/${product.slug}`}>
            Detay <IconArrowRight />
          </Link>
          <button
            className="btn btnPrimary ProductCard__addToCart"
            onClick={() => {
              cart.add(product, 1);
              toast.push({
                title: 'Sepete eklendi',
                description: product.name,
                actionHref: '/sepet',
                actionLabel: 'Sepete git',
              });
            }}
          >
            <IconCart /> Sepete ekle
          </button>
        </div>
      </div>
    </article>
  );
});
